/**
 * Helper functions for ticket capacity and availability calculations
 */

/**
 * Determines if a ticket is sold out based on capacity and availability
 * @param {Object} props - The props object containing capacity information
 * @param {boolean} props.hasTicketCapacityEnabled - Whether ticket capacity is enabled
 * @param {Object} props.slotTicketCapacity - Slot-specific ticket capacity data
 * @param {number} props.slotTicketCapacity.availability - Available tickets for this slot
 * @param {Object} props.tourTicketCapacity - Tour-wide ticket capacity data
 * @param {number} props.tourTicketCapacity.capacity - Total capacity for the tour
 * @returns {boolean} - True if the ticket is sold out, false otherwise
 */

// This method is used to check if the ticket is sold out.
// If it is, we need to show the sold out message on the guest page.
export const isTicketSoldOut = (props) => {
  const { hasTicketCapacityEnabled, isAAB } = props;

  if (!hasTicketCapacityEnabled || isAAB) return false;

  const { slotTicketCapacity, tourTicketCapacity } = props;
  const availability =
    slotTicketCapacity?.availability ?? tourTicketCapacity?.capacity;

  return availability <= 0;
};

/**
 * Calculates the remaining ticket capacity
 * @param {Object} props - The props object containing capacity information
 * @param {boolean} props.hasTicketCapacityEnabled - Whether ticket capacity is enabled
 * @param {Object} props.slotTicketCapacity - Slot-specific ticket capacity data
 * @param {number} props.slotTicketCapacity.availability - Available tickets for this slot
 * @param {Object} props.tourTicketCapacity - Tour-wide ticket capacity data
 * @param {number} props.tourTicketCapacity.capacity - Total capacity for the tour
 * @returns {number|null} - The remaining capacity, or null if capacity is not enabled
 */
export const getTicketCapacity = (props) => {
  const { hasTicketCapacityEnabled } = props;

  if (!hasTicketCapacityEnabled) return null;

  const { slotTicketCapacity, tourTicketCapacity } = props;
  return (
    slotTicketCapacity?.availability ?? tourTicketCapacity?.capacity ?? null
  );
};

/**
 * Determines if a ticket has capacity limits
 * @param {Object} props - The props object containing capacity information
 * @param {boolean} props.hasTicketCapacityEnabled - Whether ticket capacity is enabled
 * @param {Object} props.slotTicketCapacity - Slot-specific ticket capacity data
 * @param {number} props.slotTicketCapacity.availability - Available tickets for this slot
 * @param {Object} props.tourTicketCapacity - Tour-wide ticket capacity data
 * @param {number} props.tourTicketCapacity.capacity - Total capacity for the tour
 * @param {number} props.quantity - Current quantity selected
 * @returns {Object|null} - Capacity limit object or null if no limit
 */
export const getTicketCapacityLimit = (props) => {
  const { hasTicketCapacityEnabled, quantity, isAAB } = props;

  if (!hasTicketCapacityEnabled || isAAB) return null;

  const capacity = getTicketCapacity(props);

  if (!capacity || quantity <= capacity || capacity < 0) return null;

  return {
    limit: capacity,
  };
};

// AAB and guest pages share the same MultiSelectOption component.
// This method is used to check if the slot is AAB and sold out.
// If it is, we need to show the AAB message.
export const isAABandSoldOutText = (props) => {
  const { isAAB, slotTicketCapacity } = props;
  if (!isAAB || !slotTicketCapacity) return '';

  const { availability } = slotTicketCapacity;
  if (availability < 0) return `Overbooked by ${Math.abs(availability)}`;
  if (availability === 0) return 'Sold out';
  return '';
};
